import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Loader2, Trash2, Plus, RefreshCw, Save, Wrench } from 'lucide-react';
import { apiClient } from '@/utils/apiClient';
import { useTranslation } from '@/i18n';

const inputClass =
  'w-full px-3 py-2 rounded-lg border text-sm border-gray-300 dark:border-gray-600 ' +
  'bg-white dark:bg-gray-700 text-gray-900 dark:text-white ' +
  'focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors';

const primaryBtn =
  'px-4 py-2 text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 ' +
  'dark:bg-blue-500 dark:hover:bg-blue-600 transition-colors disabled:opacity-50 flex items-center gap-2';

const ghostBtn =
  'px-3 py-2 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 ' +
  'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 ' +
  'transition-colors disabled:opacity-50 flex items-center gap-2';

type Mode = 'append' | 'replace';

// A single editable override row. `multilingual`/`raw` preserve a per-language
// entry the operator authored in the file directly — we round-trip it untouched
// rather than flatten it to one language.
interface Row {
  tool: string;
  mode: Mode;
  text: string;
  multilingual: boolean;
  raw?: any;
}

function normalize(overrides: Record<string, any>): Row[] {
  return Object.entries(overrides || {}).map(([tool, entry]) => {
    if (typeof entry === 'string') {
      return { tool, mode: 'append' as Mode, text: entry, multilingual: false };
    }
    const mode: Mode = entry?.mode === 'replace' ? 'replace' : 'append';
    const text = entry?.text;
    if (typeof text === 'string') {
      return { tool, mode, text, multilingual: false };
    }
    // multilingual dict (or unexpected shape) — keep as-is, edit in file
    return { tool, mode, text: '', multilingual: true, raw: entry };
  });
}

/**
 * Free-text tool-name picker with a re-openable dropdown (BUG-1.3.7_2).
 *
 * The previous native <datalist> filtered its options by the input's current
 * value, so once a tool was picked the "list" only showed that one entry and
 * could not be re-expanded. This combobox instead shows the FULL list whenever
 * the field is empty or already holds an exact tool name (i.e. after a pick),
 * and filters by substring only while the user is typing a partial — so the
 * list can always be re-opened. Free text is preserved (unknown names are
 * allowed; the server warns about them — BUG-1.3.7_3).
 */
const ToolNameCombobox: React.FC<{
  value: string;
  onChange: (v: string) => void;
  options: string[];
  placeholder?: string;
  className?: string;
}> = ({ value, onChange, options, placeholder, className }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onDocMouseDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, []);

  const q = value.trim().toLowerCase();
  const isExact = options.some((o) => o.toLowerCase() === q);
  // Empty or a completed selection → show everything (re-expandable); a partial
  // in-progress term → filter by substring.
  const filtered = !q || isExact ? options : options.filter((o) => o.toLowerCase().includes(q));

  return (
    <div className="relative" ref={ref}>
      <input
        className={className}
        value={value}
        onChange={(e) => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        placeholder={placeholder}
        role="combobox"
        aria-expanded={open}
        aria-autocomplete="list"
      />
      {open && filtered.length > 0 && (
        <ul className="absolute z-20 mt-1 max-h-56 w-full overflow-auto rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg">
          {filtered.map((name) => (
            <li key={name}>
              <button
                type="button"
                className="w-full text-left px-3 py-1.5 text-xs font-mono text-gray-800 dark:text-gray-100 hover:bg-blue-50 dark:hover:bg-blue-900/30"
                // onMouseDown (not onClick) + preventDefault so the input's blur
                // doesn't close the list before the pick registers.
                onMouseDown={(e) => { e.preventDefault(); onChange(name); setOpen(false); }}
              >
                {name}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export const ToolDescriptionEditor: React.FC = () => {
  const { t } = useTranslation();
  const d = (t as any).dispatch || {};

  const [path, setPath] = useState<string>('');
  const [tools, setTools] = useState<string[]>([]);
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);

  // draft add-row
  const [newTool, setNewTool] = useState('');
  const [newMode, setNewMode] = useState<Mode>('append');
  const [newText, setNewText] = useState('');

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.adminGetToolDescriptions();
      setPath(res.path || '');
      setTools(res.tools || []);
      setRows(normalize(res.overrides || {}));
    } catch (err: any) {
      setError(err?.message || d.loadFailed || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [d.loadFailed]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const updateRow = (i: number, patch: Partial<Row>) =>
    setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  const removeRow = (i: number) => setRows((rs) => rs.filter((_, idx) => idx !== i));

  const addRow = () => {
    const tool = newTool.trim();
    if (!tool || !newText.trim()) return;
    setRows((rs) => [
      ...rs.filter((r) => r.tool !== tool),
      { tool, mode: newMode, text: newText, multilingual: false },
    ]);
    setNewTool('');
    setNewText('');
    setNewMode('append');
  };

  // Build the tool_name -> entry map for the write.
  const buildMap = (): Record<string, any> => {
    const map: Record<string, any> = {};
    for (const r of rows) {
      const tool = r.tool.trim();
      if (!tool) continue;
      if (r.multilingual) {
        map[tool] = r.raw; // round-trip untouched
      } else {
        if (!r.text.trim()) continue; // skip empties rather than write invalid
        map[tool] = { mode: r.mode, text: r.text };
      }
    }
    return map;
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    setWarning(null);
    try {
      const res = await apiClient.adminPutToolDescriptions(buildMap());
      setSuccess(
        (d.descSaved || 'Saved & applied') + ` — ${res.written ?? 0} override(s)`
      );
      // BUG-1.3.7_3: the server flags override keys that match no loaded tool
      // (likely typos). Non-fatal — the overrides are saved — but surface it.
      const unknown: string[] = res.unknown_tools || [];
      if (unknown.length) {
        setWarning(
          res.warning ||
          (d.descUnknownTools ||
            `${unknown.length} override(s) reference unknown tool name(s): `) +
            unknown.join(', ')
        );
      }
      await fetchAll();
    } catch (err: any) {
      setError(err?.message || d.actionFailed || 'Action failed');
    } finally {
      setSaving(false);
    }
  };

  const handleReload = async () => {
    setReloading(true);
    setError(null);
    setSuccess(null);
    setWarning(null);
    try {
      const res = await apiClient.adminReloadToolDescriptions();
      setSuccess(
        (d.descReloaded || 'Reloaded from disk')
        + ` — ${res.overrides_loaded ?? 0} override(s), ${res.tools_registered ?? 0} tools`
      );
      await fetchAll();
    } catch (err: any) {
      setError(err?.message || d.actionFailed || 'Action failed');
    } finally {
      setReloading(false);
    }
  };

  return (
    <section className="rounded-xl border border-gray-200 dark:border-gray-700 p-4 space-y-3">
      <div className="flex items-center gap-2">
        <Wrench className="h-4 w-4 text-blue-500" />
        <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
          {d.descTitle || 'MCP tool descriptions'}
        </h4>
      </div>
      <p className="text-xs text-gray-500 dark:text-gray-400">
        {d.descSubtitle ||
          'Nudge the LLM\'s tool choice in agentic mode by appending to or replacing a tool\'s description. Descriptive-only — parameters and schemas are never changed. Saved to the MCP host and applied live, no restart.'}
      </p>
      {path && (
        <p className="text-xs text-gray-400 dark:text-gray-500 font-mono truncate" title={path}>
          {path}
        </p>
      )}

      {error && (
        <div className="px-4 py-3 rounded-lg bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      )}
      {success && (
        <div className="px-4 py-3 rounded-lg bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 text-sm text-green-700 dark:text-green-300">
          {success}
        </div>
      )}
      {warning && (
        <div className="px-4 py-3 rounded-lg bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 text-sm text-amber-700 dark:text-amber-300">
          {warning}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 text-blue-500 animate-spin" />
        </div>
      ) : (
        <>
          {/* existing override rows */}
          {rows.length === 0 ? (
            <div className="text-center py-4 text-gray-500 dark:text-gray-400 text-sm">
              {d.descNone || 'No description overrides yet.'}
            </div>
          ) : (
            <div className="space-y-2">
              {rows.map((r, i) => (
                <div
                  key={`${r.tool}-${i}`}
                  className="grid grid-cols-1 sm:grid-cols-[1fr_auto_2fr_auto] gap-2 items-start rounded-lg border border-gray-200 dark:border-gray-700 p-2"
                >
                  <ToolNameCombobox
                    className={inputClass + ' font-mono text-xs'}
                    value={r.tool}
                    onChange={(v) => updateRow(i, { tool: v })}
                    options={tools}
                    placeholder={d.descToolName || 'tool name'}
                  />
                  <select
                    className={inputClass + ' sm:w-28'}
                    value={r.mode}
                    onChange={(e) => updateRow(i, { mode: e.target.value as Mode })}
                    disabled={r.multilingual}
                  >
                    <option value="append">{d.descAppend || 'append'}</option>
                    <option value="replace">{d.descReplace || 'replace'}</option>
                  </select>
                  {r.multilingual ? (
                    <div className="text-xs text-gray-400 dark:text-gray-500 italic px-3 py-2">
                      {d.descMultilingual || 'multilingual entry — edit in the file'}
                    </div>
                  ) : (
                    <textarea
                      className={inputClass + ' font-mono text-xs min-h-[44px]'}
                      value={r.text}
                      onChange={(e) => updateRow(i, { text: e.target.value })}
                    />
                  )}
                  <button
                    onClick={() => removeRow(i)}
                    className="p-2 rounded-md text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors"
                    title={d.delete || 'Delete'}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* add-row */}
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_2fr_auto] gap-2 items-start rounded-lg border border-dashed border-gray-300 dark:border-gray-600 p-2">
            <ToolNameCombobox
              className={inputClass + ' font-mono text-xs'}
              value={newTool}
              onChange={setNewTool}
              options={tools}
              placeholder={d.descToolName || 'tool name'}
            />
            <select
              className={inputClass + ' sm:w-28'}
              value={newMode}
              onChange={(e) => setNewMode(e.target.value as Mode)}
            >
              <option value="append">{d.descAppend || 'append'}</option>
              <option value="replace">{d.descReplace || 'replace'}</option>
            </select>
            <textarea
              className={inputClass + ' font-mono text-xs min-h-[44px]'}
              value={newText}
              onChange={(e) => setNewText(e.target.value)}
              placeholder={d.descTextHint || 'description text to append / replace'}
            />
            <button
              onClick={addRow}
              disabled={!newTool.trim() || !newText.trim()}
              className="p-2 rounded-md text-blue-600 hover:text-blue-800 hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors disabled:opacity-40"
              title={d.add || 'Add'}
            >
              <Plus className="h-4 w-4" />
            </button>
          </div>

          <div className="flex justify-between items-center pt-1">
            <button className={ghostBtn} onClick={handleReload} disabled={reloading || saving}>
              {reloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              {d.descReload || 'Reload from disk'}
            </button>
            <button className={primaryBtn} onClick={handleSave} disabled={saving || reloading}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              {d.descSave || 'Save & apply'}
            </button>
          </div>
        </>
      )}
    </section>
  );
};
